import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAnalyticsAlerts,
  fetchAnalyticsConflicts,
  fetchAnalyticsDemandForecast,
  fetchAnalyticsOverview,
  fetchAnalyticsPatterns,
  fetchAnalyticsRestockPlan,
  fetchAnalyticsTopPerformers,
  fetchAnalyticsTurnover,
  fetchBranches,
  fetchCreditBalance,
  fetchDashboard,
  fetchInventory,
  generateProductImage as requestGenerateProductImage,
  fetchNotificationInbox,
  fetchNotificationDevices,
  fetchNotificationPreferences,
  markNotificationInboxRead,
  fetchSalesHistory,
  fetchStaff,
  fetchWorkspaceSnapshot,
  cleanProductImage as requestCleanProductImage,
  selectSessionBranch,
} from "@/lib/shopApi";
import {
  deleteWebPendingSyncQueueItem,
  commitWebSaleBundle,
  enqueueWebSyncOperation,
  getWebPendingSyncQueueItems,
  loadWebOfflineWorkspace,
  processWebSyncQueue,
  retryFailedWebSyncOperations,
  saveWebDebtPayment,
  saveWebProduct,
  saveWebStockMovement,
  seedWebOfflineWorkspace,
} from "@/lib/offline/webOfflineStore";
import { getLocalizationSnapshot } from "@/lib/localization";
import { notifyInsufficientCredits, notifySubscriptionReadOnly } from "@/lib/apiClient";
import { hasPermission } from "@/lib/permissions";
import { isSubscriptionReadOnly } from "@/lib/subscriptionFlow";
import { getWorkspaceSessionIdentity } from "./workspaceIdentity";
import { mergeWorkspaceSales } from "./workspaceMerge";
import { useAuth } from "./authState";
import { ProductLimitUpgradeDialog } from "@/components/subscription/ProductLimitUpgradeDialog";
import { getProductLimit, ProductLimitError } from "@/lib/productLimits";
import { createProductUpsertIdentity } from "@/lib/productMutation";

const WorkspaceCtx = createContext(null);
const DEFAULT_TZ_OFFSET = 60;
const NOTIFICATION_READ_STORAGE_KEY = "shopbook:notification-read-state";
const SUBSCRIPTION_READ_ONLY_MESSAGE =
  "Your subscription is read-only. Renew it to create, update, or delete records.";
const PRODUCT_IMAGE_COST_UNITS = 5_000;

function assertSubscriptionWritable(session) {
  if (isSubscriptionReadOnly(session?.subscription)) {
    notifySubscriptionReadOnly();
    throw new Error(SUBSCRIPTION_READ_ONLY_MESSAGE);
  }
}

function assertSufficientCredits(balance, requiredUnits, feature) {
  const availableUnits = Number(balance?.availableUnits);
  if (!Number.isFinite(availableUnits) || availableUnits >= requiredUnits) return;

  notifyInsufficientCredits({ availableUnits, feature, requiredUnits });
  const error = new Error("Insufficient credits");
  error.code = "INSUFFICIENT_CREDITS";
  throw error;
}

const EMPTY_DASHBOARD = {
  generatedAt: "",
  lowStockCount: 0,
  recentTransactions: [],
  saleCount: 0,
  todayTotal: 0,
  topProducts: [],
  trend30d: [],
  trend7d: [],
};

const EMPTY_ANALYTICS = {
  alerts: [],
  buyingPatterns: [],
  conflicts: [],
  demandForecast: [],
  overviewCards: [],
  restockPlan: [],
  topPerformers: [],
  turnover: { fastMovers: [], slowMovers: [] },
};

const EMPTY_NOTIFICATIONS = {
  inbox: [],
  preferences: [],
};

const EMPTY_CREDITS = {
  availableCredits: "0",
  availableUnits: 0,
  balanceCredits: "0",
  balanceUnits: 0,
  reservedCredits: "0",
  reservedUnits: 0,
};

function mapProduct(product) {
  const { messages } = getLocalizationSnapshot();
  return {
    barcode: product.barcode ?? null,
    category: product.category ?? messages.common.general,
    cost: product.cost ?? 0,
    id: product.id,
    lastSoldAt: product.lastSoldAt ?? "",
    lowStockAt: product.lowStockAt ?? 0,
    name: product.name,
    price: product.price ?? 0,
    sellableStock: product.sellableStock === undefined ? undefined : product.sellableStock,
    soldToday: product.soldToday ?? 0,
    status: product.status ?? "Active",
    stock: product.stock ?? null,
    stockTrackingEnabled: product.stockTrackingEnabled ?? product.stock !== null,
    url: product.url ?? null,
    urlStatus: product.urlStatus ?? "pending",
  };
}

function mapSale(sale) {
  const { messages } = getLocalizationSnapshot();
  return {
    amountPaid: sale.amountPaid ?? 0,
    balanceDue: sale.balanceDue ?? Math.max(0, (sale.total ?? 0) - (sale.amountPaid ?? 0)),
    cashier: sale.cashier ?? messages.common.unknown,
    createdAt: sale.createdAt ?? sale.createdAtIso ?? sale.createdAtISO ?? new Date().toISOString(),
    debtId: sale.debtId ?? null,
    id: sale.id,
    items: (sale.items ?? []).map((item) => ({
      cartId: item.cartId ?? item.productId ?? item.name,
      name: item.name,
      productId: item.productId,
      quantity: item.quantity ?? 1,
      stock: item.stock ?? null,
      unitPrice: item.unitPrice ?? 0,
    })),
    location: sale.location ?? null,
    paymentStatus: sale.paymentStatus ?? "Paid",
    syncState: sale.syncState ?? "Synced",
    total: sale.total ?? 0,
  };
}

function mapDebt(debt) {
  const { messages } = getLocalizationSnapshot();
  return {
    amountOwed: debt.amountOwed ?? 0,
    amountPaid: debt.amountPaid ?? 0,
    branchId: debt.branchId ?? "",
    customerName: debt.customerName ?? messages.common.walkIn,
    dueDate: debt.dueDateIso ?? debt.dueDate ?? new Date().toISOString().slice(0, 10),
    id: debt.id,
    itemSummary: debt.itemSummary ?? "",
    phone: debt.phone ?? "—",
    saleId: debt.saleId ?? null,
    status: debt.status ?? "Unpaid",
  };
}

function mapDebtPayment(payment) {
  const { messages } = getLocalizationSnapshot();
  return {
    amount: payment.amount ?? 0,
    branchId: payment.branchId ?? "",
    createdAt: payment.createdAtIso ?? payment.createdAt ?? new Date().toISOString(),
    customerName: payment.customerName ?? messages.common.walkIn,
    debtId: payment.debtId,
    id: payment.id,
    saleId: payment.saleId ?? null,
  };
}

function createClientId(prefix) {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now()}_${randomPart}`;
}

function normalizeName(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function buildProductPayload(product) {
  const trackingEnabled = Boolean(product.stockTrackingEnabled ?? product.stock !== null);
  return {
    barcode: product.barcode ? String(product.barcode).trim() : null,
    cost: Number(product.cost || 0),
    id: product.id,
    lastSoldAt: product.lastSoldAt || "",
    lowStockAt: Number(product.lowStockAt || 0),
    name: String(product.name || "").trim(),
    normalizedName: normalizeName(product.name),
    price: Number(product.price || 0),
    status: product.status || "Active",
    stock: trackingEnabled ? Number(product.stock || 0) : null,
    stockTrackingEnabled: trackingEnabled,
    url: product.url ? String(product.url).trim() : null,
    urlStatus: product.urlStatus || "pending",
  };
}

function mapBranch(branch, session, staffCount) {
  return {
    city: branch.city ?? "",
    declaredLatitude: branch.declaredLatitude ?? null,
    declaredLongitude: branch.declaredLongitude ?? null,
    id: branch.id,
    inferredLatitude: branch.inferredLatitude ?? null,
    inferredLongitude: branch.inferredLongitude ?? null,
    isActive: Boolean(branch.isActive),
    locationConfidence: branch.locationConfidence ?? "low",
    locationSource: branch.locationSource ?? "unknown",
    locationUpdatedAt: branch.locationUpdatedAt ?? null,
    name: branch.name,
    role: session?.role ?? "Member",
    staffCount,
    staffLoginLocationRestricted: Boolean(branch.staffLoginLocationRestricted),
  };
}

function mapNotificationInbox(items) {
	return items.map((item) => {
		const timestampCandidates = [item.createdAtIso, item.createdAtISO, item.createdAt];
		const createdAt = timestampCandidates.find((value) => value && Number.isFinite(new Date(value).getTime())) ?? null;
		return {
    actionLabel: item.actionLabel ?? "",
    body: item.body,
		createdAt,
    createdAtIso: item.createdAtIso,
    data: item.data ?? {},
    group: item.group,
    id: item.id,
    isRead: Boolean(item.isRead),
    readAt: item.readAt ?? "",
    tone: item.tone,
    title: item.title,
    type: item.type,
		};
	});
}

function buildLocalNotifications(products, sync) {
  const { messages } = getLocalizationSnapshot();
  const copy = messages.notificationsPage;
  const createdAt = new Date().toISOString();
  const syncNotification = (sync?.queuedCount || sync?.failedCount)
    ? [{
        actionLabel: copy.reviewSync,
        body: copy.queuedRecords(sync.queuedCount || 0),
        createdAt,
        group: copy.today,
        id: "local-sync-pending",
        tone: sync.failedCount ? "danger" : "info",
        title: sync.failedCount ? copy.syncNeedsAttention : copy.syncPending,
        type: "sync",
      }]
    : [];

  const stockNotifications = products
    .filter((product) => product.stock !== null && product.stock <= product.lowStockAt)
    .map((product) => ({
      actionLabel: copy.addStock,
      body: product.stock === 0
        ? copy.outOfStockBody(product.name)
        : copy.lowStockBody(product.name, product.stock),
      createdAt,
      group: copy.today,
      id: `local-stock-${product.id}`,
      tone: product.stock === 0 ? "danger" : "warning",
      title: product.stock === 0 ? copy.stockOut : copy.lowStock,
      type: product.stock === 0 ? "stock-out" : "low-stock",
    }));

  return [...syncNotification, ...stockNotifications];
}

function mergeNotifications(remoteNotifications, localNotifications) {
  const seen = new Set();
  return [...localNotifications, ...remoteNotifications].filter((notification) => {
    if (seen.has(notification.id)) {
      return false;
    }
    seen.add(notification.id);
    return true;
  });
}

function readNotificationReadState() {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(NOTIFICATION_READ_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeNotificationReadState(state) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(NOTIFICATION_READ_STORAGE_KEY, JSON.stringify(state));
}

function getSessionNotificationReads(sessionId) {
  if (!sessionId) {
    return [];
  }

  const stored = readNotificationReadState();
  return Array.isArray(stored[sessionId]) ? stored[sessionId] : [];
}

function persistSessionNotificationReads(sessionId, reads) {
  if (!sessionId) {
    return;
  }

  const stored = readNotificationReadState();
  stored[sessionId] = reads;
  writeNotificationReadState(stored);
}

function buildEmptyLoadingState() {
  return {
    analytics: EMPTY_ANALYTICS,
    assistant: { promptCount: 0 },
    branches: [],
    credits: EMPTY_CREDITS,
    currentBranch: null,
    debtPayments: [],
    debts: [],
    dashboard: EMPTY_DASHBOARD,
    inventory: { all: [], today: [], counts: { all: 0, in_stock: 0, low_stock: 0, stock_out: 0 } },
    inventoryAllocations: [],
    loading: true,
    notifications: EMPTY_NOTIFICATIONS,
    notificationDevices: [],
    notificationPrefs: [],
    products: [],
    sales: [],
    staff: [],
    stockMovements: [],
    sync: { failedCount: 0, isProcessing: false, lastError: null, lastSyncedAt: null, nextRetryAt: null, online: false, queuedCount: 0 },
    syncQueue: [],
  };
}

function buildInventoryFromProducts(products) {
  return {
    all: products,
    counts: {
      all: products.length,
      in_stock: products.filter((p) => p.stock !== null && p.stock > p.lowStockAt).length,
      low_stock: products.filter((p) => p.stock !== null && p.stock > 0 && p.stock <= p.lowStockAt).length,
      stock_out: products.filter((p) => p.stock === 0).length,
    },
    today: products.filter((p) => (p.soldToday ?? 0) > 0),
  };
}

function mergeSyncState(remoteSync, localSync) {
  return {
    failedCount: localSync?.failedCount ?? remoteSync?.failedCount ?? 0,
    isProcessing: Boolean(localSync?.isProcessing || remoteSync?.isProcessing),
    lastError: localSync?.lastError ?? remoteSync?.lastError ?? null,
    lastSyncedAt: localSync?.lastSyncedAt ?? remoteSync?.lastSyncedAt ?? null,
    nextRetryAt: localSync?.nextRetryAt ?? remoteSync?.nextRetryAt ?? null,
    online: typeof navigator === "undefined" ? Boolean(remoteSync?.online ?? true) : navigator.onLine,
    queuedCount: localSync?.queuedCount ?? remoteSync?.queuedCount ?? 0,
  };
}

function applyWorkspaceSnapshot(prev, {
  analytics,
  branches,
  currentBranch,
  credits,
  dashboard,
  debtPayments,
  debts,
  devices,
  localSync,
  notifications,
  preferences,
  products,
  inventoryAllocations,
  sales,
  staff,
  stockMovements,
  sync,
  syncQueue,
}) {
  const nextProducts = products ?? prev.products;
  const nextSync = mergeSyncState(sync ?? prev.sync, localSync);
  const nextNotifications = notifications
    ? {
        ...notifications,
        inbox: mergeNotifications(notifications.inbox ?? [], buildLocalNotifications(nextProducts, nextSync)),
      }
    : {
        ...prev.notifications,
        inbox: mergeNotifications(prev.notifications.inbox ?? [], buildLocalNotifications(nextProducts, nextSync)),
      };

  return {
    ...prev,
    analytics: analytics ?? prev.analytics,
    branches: branches ?? prev.branches,
    credits: credits ?? prev.credits,
    currentBranch: currentBranch ?? prev.currentBranch,
    debtPayments: debtPayments ?? prev.debtPayments,
    debts: debts ?? prev.debts,
    dashboard: dashboard ?? prev.dashboard,
    inventory: products ? buildInventoryFromProducts(products) : prev.inventory,
    inventoryAllocations: inventoryAllocations ?? prev.inventoryAllocations,
    loading: false,
    notifications: nextNotifications,
    notificationDevices: devices ?? prev.notificationDevices,
    notificationPrefs: preferences ?? prev.notificationPrefs,
    products: nextProducts,
    sales: sales ?? prev.sales,
    staff: staff ?? prev.staff,
    stockMovements: stockMovements ?? prev.stockMovements,
    sync: nextSync,
    syncQueue: syncQueue ?? prev.syncQueue,
  };
}

export function WorkspaceProvider({ children }) {
  const [productLimitError, setProductLimitError] = useState(null);
  const { session, updateSession } = useAuth();
  const [state, setState] = useState(() => buildEmptyLoadingState());
  const [readNotificationIds, setReadNotificationIds] = useState([]);
  const workspaceSessionSignature = getWorkspaceSessionIdentity(session);
  const workspaceSessionRef = useRef({
    session,
    signature: workspaceSessionSignature,
  });
  if (workspaceSessionRef.current.signature !== workspaceSessionSignature) {
    workspaceSessionRef.current = {
      session,
      signature: workspaceSessionSignature,
    };
  }
  const workspaceSession = workspaceSessionRef.current.session;

  useEffect(() => {
    setReadNotificationIds(getSessionNotificationReads(session?.sessionId));
  }, [session?.sessionId]);

  const loadWorkspace = useCallback(async () => {
    const session = workspaceSession;
    if (!session?.sessionId) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));
    const [localWorkspace, localSyncQueue] = await Promise.all([
      loadWebOfflineWorkspace(session).catch(() => null),
      getWebPendingSyncQueueItems(session).catch(() => []),
    ]);
    if (localWorkspace && (
      localWorkspace.products.length ||
      localWorkspace.sales.length ||
      localWorkspace.debts.length ||
      localWorkspace.debtPayments.length
    )) {
      setState((prev) => applyWorkspaceSnapshot(prev, {
        debtPayments: localWorkspace.debtPayments,
        debts: localWorkspace.debts.map(mapDebt),
        localSync: localWorkspace.sync,
        inventoryAllocations: localWorkspace.inventoryAllocations,
        products: localWorkspace.products.map(mapProduct),
        sales: localWorkspace.sales.map(mapSale),
        stockMovements: localWorkspace.stockMovements,
        syncQueue: localSyncQueue,
      }));
    }

    const tzOffsetMinutes = DEFAULT_TZ_OFFSET;
    const canAccessAnalytics = hasPermission(session, "analytics");
    const canAccessBranches = hasPermission(session, "branch_management");
    const canAccessHistory = hasPermission(session, "history");
    const canAccessInventory = hasPermission(session, "sell") || hasPermission(session, "inventory");
    const canAccessNotifications = hasPermission(session, "notifications");
    const canAccessStaff = hasPermission(session, "staff_access");
    const [snapshot, dashboard, inventoryAll, history, branches, staff, notifications, devices, preferences, creditBalance, overview, restockPlan, topPerformers, turnover, patterns, demandForecast, conflicts, alerts] = await Promise.all([
      fetchWorkspaceSnapshot(session.sessionId, tzOffsetMinutes).catch(() => null),
      canAccessAnalytics ? fetchDashboard(session.sessionId, tzOffsetMinutes).catch(() => null) : null,
      canAccessInventory ? fetchInventory(session.sessionId, "all", "", tzOffsetMinutes).catch(() => null) : null,
      canAccessHistory ? fetchSalesHistory(session.sessionId, 250).catch(() => null) : null,
      canAccessBranches ? fetchBranches(session.sessionId).catch(() => null) : null,
      canAccessStaff ? fetchStaff(session.sessionId).catch(() => null) : null,
      canAccessNotifications ? fetchNotificationInbox(session.sessionId).catch(() => null) : null,
      canAccessNotifications ? fetchNotificationDevices(session.sessionId).catch(() => null) : null,
      canAccessNotifications ? fetchNotificationPreferences(session.sessionId).catch(() => null) : null,
      fetchCreditBalance(session.sessionId).catch(() => null),
      canAccessAnalytics ? fetchAnalyticsOverview(session.sessionId).catch(() => null) : null,
      canAccessAnalytics ? fetchAnalyticsRestockPlan(session.sessionId).catch(() => null) : null,
      canAccessAnalytics ? fetchAnalyticsTopPerformers(session.sessionId).catch(() => null) : null,
      canAccessAnalytics ? fetchAnalyticsTurnover(session.sessionId).catch(() => null) : null,
      canAccessAnalytics ? fetchAnalyticsPatterns(session.sessionId).catch(() => null) : null,
      canAccessAnalytics ? fetchAnalyticsDemandForecast(session.sessionId).catch(() => null) : null,
      canAccessAnalytics ? fetchAnalyticsConflicts(session.sessionId).catch(() => null) : null,
      canAccessAnalytics ? fetchAnalyticsAlerts(session.sessionId).catch(() => null) : null,
    ]);

    const hasRemoteWorkspace = Boolean(snapshot || inventoryAll || history || dashboard);
    if (!hasRemoteWorkspace && localWorkspace) {
      setState((prev) => applyWorkspaceSnapshot(prev, {
        debtPayments: localWorkspace.debtPayments,
        debts: localWorkspace.debts.map(mapDebt),
        localSync: localWorkspace.sync,
        inventoryAllocations: localWorkspace.inventoryAllocations,
        products: localWorkspace.products.map(mapProduct),
        sales: localWorkspace.sales.map(mapSale),
        stockMovements: localWorkspace.stockMovements,
        syncQueue: localSyncQueue,
      }));
      return;
    }

    const inventoryAllocations = snapshot?.workspace?.inventoryAllocations ?? localWorkspace?.inventoryAllocations ?? [];
    const products = (inventoryAll?.inventory?.products ?? snapshot?.workspace?.products ?? localWorkspace?.products ?? [])
      .map(mapProduct)
      .map((product) => {
        if (!product.stockTrackingEnabled) return { ...product, sellableStock: null };
        const allocation = inventoryAllocations.find((item) => (
          item.productId === product.id && item.userId === session.userId
        ));
        return {
          ...product,
          sellableStock: allocation?.grantId
            ? Number(allocation.allocatedQuantity || 0)
            : product.sellableStock === undefined
              ? product.stock
              : product.sellableStock,
        };
      });
    const remoteSales = history?.history?.sales ?? snapshot?.workspace?.transactions ?? [];
    const sales = mergeWorkspaceSales(remoteSales, localWorkspace?.sales ?? []).map(mapSale);
    const debts = (snapshot?.workspace?.debts ?? localWorkspace?.debts ?? []).map(mapDebt);
    const debtPayments = (snapshot?.workspace?.debtPayments ?? localWorkspace?.debtPayments ?? []).map(mapDebtPayment);
    const stockMovements = snapshot?.workspace?.stockMovements ?? localWorkspace?.stockMovements ?? [];
    const branchList = branches?.branches ?? snapshot?.workspace?.branches ?? session?.branches ?? [];
    const staffList = staff?.staff ?? [];
    const currentBranchId = session?.selectedBranchId;
    const normalizedBranches = branchList.map((branch) => mapBranch(
      branch,
      session,
      staffList.filter((member) => member.branchId === branch.id).length
    ));
    const currentBranch = normalizedBranches.find((branch) => branch.id === currentBranchId) ?? normalizedBranches[0] ?? null;

    const analyticsState = {
        alerts: alerts?.analyticsAlerts?.alerts ?? [],
        buyingPatterns: patterns?.buyingPatterns?.patterns ?? [],
        conflicts: conflicts?.analyticsInventoryConflicts?.items ?? conflicts?.inventoryConflicts ?? [],
        demandForecast: demandForecast?.demandForecast?.forecasts ?? [],
        overviewCards: overview?.analytics?.cards ?? [],
        restockPlan: restockPlan?.restockPlan?.recommendations ?? [],
        topPerformers: topPerformers?.topPerformers?.items ?? [],
        turnover: {
          fastMovers: turnover?.turnover?.fastMovers ?? [],
          slowMovers: turnover?.turnover?.slowMovers ?? [],
        },
      };
    // fetchNotificationPreferences already returns the canonical route list used
    // by mobile. Keep it intact so web does not recreate the retired legacy
    // billing-email/billing-push rows or overwrite route defaults.
    const preferenceState = Array.isArray(preferences) ? preferences : null;
    const notificationState = {
      inbox: mapNotificationInbox(notifications?.notifications ?? []),
      preferences: preferenceState ?? [],
    };
    const syncState = snapshot?.workspace?.syncSummary ?? localWorkspace?.sync ?? { failedCount: 0, isProcessing: false, lastError: null, lastSyncedAt: null, nextRetryAt: null, online: false, queuedCount: 0 };

    await seedWebOfflineWorkspace(session, {
      debtPayments,
      debts,
      inventoryAllocations,
      products,
      sales,
      stockMovements,
    }).catch(() => null);
    const [nextLocalWorkspace, nextSyncQueue] = await Promise.all([
      loadWebOfflineWorkspace(session).catch(() => localWorkspace),
      getWebPendingSyncQueueItems(session).catch(() => localSyncQueue),
    ]);

    setState((prev) => applyWorkspaceSnapshot(prev, {
      analytics: analyticsState,
      branches: normalizedBranches,
      credits: creditBalance?.credits ?? prev.credits ?? EMPTY_CREDITS,
      currentBranch,
      debtPayments,
      debts,
      dashboard: dashboard?.dashboard ?? EMPTY_DASHBOARD,
      devices: devices?.devices ?? [],
      localSync: nextLocalWorkspace?.sync,
      inventoryAllocations,
      notifications: notificationState,
      preferences: preferenceState,
      notificationDevices: devices?.devices ?? [],
      products,
      sales,
      staff: staffList,
      stockMovements,
      sync: syncState,
      syncQueue: nextSyncQueue,
    }));
  }, [workspaceSession]);

  useEffect(() => {
    loadWorkspace().catch((error) => {
      console.error(error);
      setState((prev) => ({ ...prev, loading: false }));
    });
  }, [loadWorkspace]);

  useEffect(() => {
    if (!session?.sessionId || typeof window === "undefined") {
      return undefined;
    }

    const syncWhenOnline = () => {
      processWebSyncQueue(session)
        .then(() => loadWorkspace())
        .catch((error) => console.error(error));
    };

    window.addEventListener("online", syncWhenOnline);
    return () => window.removeEventListener("online", syncWhenOnline);
  }, [loadWorkspace, session]);

  useEffect(() => {
    if (!session?.sessionId || !state.notifications.inbox.length) {
      return;
    }

    const allowedIds = new Set(state.notifications.inbox.map((item) => item.id));
    const nextReadIds = readNotificationIds.filter((id) => allowedIds.has(id));

    if (nextReadIds.length !== readNotificationIds.length) {
      setReadNotificationIds(nextReadIds);
      persistSessionNotificationReads(session.sessionId, nextReadIds);
    }
  }, [readNotificationIds, session?.sessionId, state.notifications.inbox]);

  const setCurrentBranch = useCallback(async (branchId) => {
    if (!session?.sessionId || branchId === session.selectedBranchId) return;
    const response = await selectSessionBranch(session.sessionId, branchId);
    updateSession({ ...session, ...(response?.session ?? {}), selectedBranchId: branchId });
  }, [session, updateSession]);

  const setDebts = useCallback((updater) => {
    setState((prev) => {
      const nextDebts = typeof updater === "function" ? updater(prev.debts) : updater;
      return { ...prev, debts: nextDebts };
    });
  }, []);

  const completeSale = useCallback(async (sale) => {
    if (!session?.sessionId) {
      throw new Error(getLocalizationSnapshot().messages.analytics.sessionNotReady);
    }
    assertSubscriptionWritable(session);

    const idempotencyKey = sale.idempotencyKey || `idem_${sale.id}`;
    const isOnlineExecution = typeof navigator === "undefined" ? true : navigator.onLine;
    if (isOnlineExecution && state.syncQueue.some((operation) => (
      operation.operationType === "SALE_RECORDED" && ["QUEUED", "PROCESSING", "FAILED"].includes(operation.status)
    ))) {
      processWebSyncQueue(session).then(() => loadWorkspace()).catch(() => null);
      throw new Error("Pending offline sales must finish syncing before a new online sale can be recorded.");
    }
    const offlineInventoryGrants = [];
    if (!isOnlineExecution) {
      const seenProducts = new Set();
      for (const item of sale.items) {
        if (item.stock === null || item.stock === undefined || seenProducts.has(item.productId)) continue;
        seenProducts.add(item.productId);
        const grant = state.inventoryAllocations.find((allocation) => (
          allocation.productId === item.productId && allocation.userId === session.userId
        ));
        if (!grant?.grantId || Number(grant.version || 0) <= 0 || !grant.expiresAt || Date.parse(grant.expiresAt) <= Date.now()) {
          throw new Error(`${item.name} has no current offline selling allowance. Reconnect to refresh inventory.`);
        }
        offlineInventoryGrants.push({ grantId: grant.grantId, productId: item.productId, version: Number(grant.version) });
      }
    }
    const salePayload = {
      amountPaid: Number(sale.amountPaid || 0),
      balanceDue: Number(sale.balanceDue || 0),
      cashier: sale.cashier || session.shopName || getLocalizationSnapshot().messages.common.unknown,
      createdAtIso: sale.createdAt || new Date().toISOString(),
      debtId: sale.debtId || null,
      id: sale.id,
      inventoryExecutionMode: isOnlineExecution ? "online" : "offline",
      items: sale.items.map((item) => ({
        cartId: item.cartId || `${sale.id}_${item.productId}`,
        name: item.name,
        productId: item.productId,
        quantity: Number(item.quantity || 1),
        stock: item.stock === null || item.stock === undefined ? null : Number(item.stock),
        unitPrice: Number(item.unitPrice || 0),
      })),
      location: sale.location,
      offlineInventoryGrants,
      paymentStatus: sale.paymentStatus,
      total: Number(sale.total || 0),
    };

    const storedSale = { ...salePayload, createdAt: salePayload.createdAtIso, syncState: "Pending" };
    const localStockMovements = salePayload.items
      .filter((item) => item.stock !== null)
      .map((item) => ({
        id: `stock-out-${salePayload.id}-${item.cartId}`,
        movementId: `stock-out-${salePayload.id}-${item.cartId}`,
        occurredAt: salePayload.createdAtIso,
        productId: item.productId,
        quantity: item.quantity,
        saleId: salePayload.id,
        type: "OUT",
      }));
    const debtPayload = sale.debt ? { ...sale.debt } : null;
    if (debtPayload) delete debtPayload.idempotencyKey;
    await commitWebSaleBundle(session, {
      debt: debtPayload,
      debtIdempotencyKey: sale.debt?.idempotencyKey || (debtPayload ? `idem_${debtPayload.id}` : ""),
      sale: storedSale,
      saleIdempotencyKey: idempotencyKey,
      stockMovements: localStockMovements,
    });

    setState((prev) => ({
      ...prev,
      debts: sale.debt ? [mapDebt(sale.debt), ...prev.debts] : prev.debts,
      sales: [mapSale({ ...salePayload, syncState: "Pending" }), ...prev.sales],
      products: prev.products.map((product) => {
        const item = sale.items.find((cartItem) => cartItem.productId === product.id);
        if (!item || product.stock === null) return product;
        return {
          ...product,
          sellableStock: product.sellableStock === undefined || product.sellableStock === null
            ? product.sellableStock
            : Math.max(0, product.sellableStock - item.quantity),
          stock: Math.max(0, product.stock - item.quantity),
          soldToday: (product.soldToday || 0) + item.quantity,
        };
      }),
      inventoryAllocations: prev.inventoryAllocations.map((allocation) => {
        const item = sale.items.find((cartItem) => cartItem.productId === allocation.productId);
        if (!item || allocation.userId !== session.userId) return allocation;
        return {
          ...allocation,
          allocatedQuantity: Math.max(0, Number(allocation.allocatedQuantity || 0) - item.quantity),
          updatedAt: salePayload.createdAtIso,
        };
      }),
      stockMovements: [...localStockMovements, ...prev.stockMovements],
      sync: { ...prev.sync, queuedCount: (prev.sync.queuedCount || 0) + (sale.debt ? 2 : 1), online: typeof navigator === "undefined" ? true : navigator.onLine },
      syncQueue: [
        ...prev.syncQueue,
        {
          aggregateId: salePayload.id,
          attempts: 0,
          createdAt: salePayload.createdAtIso,
          detail: getLocalizationSnapshot().messages.settings.queueSaleItems(salePayload.items.length),
          id: `sync-sale-${salePayload.id}`,
          itemLabel: salePayload.items.map((item) => item.name).join(", ") || getLocalizationSnapshot().messages.settings.queuePendingSale,
          lastError: null,
          operationType: "SALE_RECORDED",
          quantityDelta: null,
          status: "QUEUED",
          updatedAt: salePayload.createdAtIso,
        },
      ],
    }));
    processWebSyncQueue(session).then(() => loadWorkspace()).catch(() => loadWorkspace());
  }, [loadWorkspace, session, state.inventoryAllocations, state.syncQueue]);

  const saveProduct = useCallback(async (productInput, options = {}) => {
    if (!session?.sessionId) {
      throw new Error(getLocalizationSnapshot().messages.analytics.sessionNotReady);
    }
    assertSubscriptionWritable(session);

    const now = new Date().toISOString();
    const product = buildProductPayload({
      ...productInput,
      id: productInput.id || createClientId("p"),
      status: productInput.status || "Active",
    });

    const existingProduct = state.products.find((item) => item.id === product.id);
    const isNewActiveProduct = product.status === "Active" && !existingProduct;
    const activeProductCount = state.products.filter((item) => item.status === "Active").length;
    const productLimit = getProductLimit(session);
    if (isNewActiveProduct && activeProductCount >= productLimit) {
      const limitError = new ProductLimitError({
        currentCount: activeProductCount,
        limit: productLimit,
        planId: session?.subscription?.plan?.id || "solo",
        planName: session?.subscription?.plan?.name || "Solo",
      });
      setProductLimitError(limitError);
      throw limitError;
    }

    // Idempotency identifies one user mutation, not the product aggregate.
    // Reusing the product ID here causes the server to replay the first-ever
    // upsert and silently discard later edits (for example, completing setup).
    const mutationIdentity = createProductUpsertIdentity(
      product.id,
      options.mutationId || createClientId("mutation"),
    );
    const productIdempotencyKey = options.idempotencyKey || mutationIdentity.idempotencyKey;
    await saveWebProduct(session, product);
    await enqueueWebSyncOperation(session, {
      aggregateId: product.id,
      id: options.operationId || mutationIdentity.operationId,
      idempotencyKey: productIdempotencyKey,
      operationType: "PRODUCT_UPSERT",
      payload: product,
    });

    let stockMovementQueued = false;

    if (options.initialStock && Number(options.initialStock) > 0) {
      const movementId = createClientId("sm");
      const movement = {
        movementId,
        occurredAt: now,
        productId: product.id,
        quantity: Number(options.initialStock),
        saleId: null,
        type: "IN",
      };
      await saveWebStockMovement(session, movement);
      await enqueueWebSyncOperation(session, {
        aggregateId: movementId,
        id: `sync-stock-${movementId}`,
        idempotencyKey: `idem_${movementId}`,
        operationType: "STOCK_MOVEMENT_RECORDED",
        payload: movement,
      });
      stockMovementQueued = true;
    }

    const stockDelta = Number(options.stockDelta || 0);
    if (stockDelta !== 0) {
      const movementId = createClientId("sm");
      const movement = {
        movementId,
        occurredAt: now,
        productId: product.id,
        quantity: Math.abs(stockDelta),
        saleId: null,
        type: stockDelta > 0 ? "IN" : "OUT",
      };
      await saveWebStockMovement(session, movement);
      await enqueueWebSyncOperation(session, {
        aggregateId: movementId,
        id: `sync-stock-${movementId}`,
        idempotencyKey: `idem_${movementId}`,
        operationType: "STOCK_MOVEMENT_RECORDED",
        payload: movement,
      });
      stockMovementQueued = true;
    }

    setState((prev) => {
      const mapped = mapProduct(product);
      const exists = prev.products.some((item) => item.id === mapped.id);
      return {
        ...prev,
        products: exists
          ? prev.products.map((item) => (item.id === mapped.id
            ? {
                ...item,
                ...mapped,
                sellableStock: mapped.sellableStock === undefined ? item.sellableStock : mapped.sellableStock,
              }
            : item))
          : [{
              ...mapped,
              sellableStock: mapped.sellableStock === undefined ? mapped.stock : mapped.sellableStock,
            }, ...prev.products],
        sync: { ...prev.sync, queuedCount: (prev.sync.queuedCount || 0) + 1 + (stockMovementQueued ? 1 : 0), online: typeof navigator === "undefined" ? true : navigator.onLine },
      };
    });
    processWebSyncQueue(session).then(() => loadWorkspace()).catch(() => loadWorkspace());
    return product;
  }, [loadWorkspace, session, state.products]);

  const addStock = useCallback(async (productId, quantity) => {
    if (!session?.sessionId) {
      throw new Error(getLocalizationSnapshot().messages.analytics.sessionNotReady);
    }
    assertSubscriptionWritable(session);

    const amount = Number(quantity || 0);
    if (!Number.isInteger(amount) || amount <= 0) {
      throw new Error(getLocalizationSnapshot().messages.common.enterStockQuantity);
    }

    const movementId = createClientId("sm");
    const movement = {
      movementId,
      occurredAt: new Date().toISOString(),
      productId,
      quantity: amount,
      saleId: null,
      type: "IN",
    };
    await saveWebStockMovement(session, movement);
    await enqueueWebSyncOperation(session, {
      aggregateId: movementId,
      id: `sync-stock-${movementId}`,
      idempotencyKey: `idem_${movementId}`,
      operationType: "STOCK_MOVEMENT_RECORDED",
      payload: movement,
    });

    setState((prev) => ({
      ...prev,
      products: prev.products.map((product) => (
        product.id === productId && product.stock !== null
          ? {
              ...product,
              sellableStock: product.sellableStock === undefined || product.sellableStock === null
                ? product.sellableStock
                : product.sellableStock + amount,
              stock: product.stock + amount,
            }
          : product
      )),
      sync: { ...prev.sync, queuedCount: (prev.sync.queuedCount || 0) + 1, online: typeof navigator === "undefined" ? true : navigator.onLine },
    }));
    processWebSyncQueue(session).then(() => loadWorkspace()).catch(() => loadWorkspace());
  }, [loadWorkspace, session]);

  const archiveProduct = useCallback(async (productId) => {
    const product = state.products.find((item) => item.id === productId);
    if (!product) return;
    await saveProduct({ ...product, status: "Archived" });
  }, [saveProduct, state.products]);

  const generateProductImage = useCallback(async (productId, payload = {}) => {
    if (!session?.sessionId) {
      throw new Error(getLocalizationSnapshot().messages.analytics.sessionNotReady);
    }
    assertSubscriptionWritable(session);
    assertSufficientCredits(state.credits, PRODUCT_IMAGE_COST_UNITS, "product_image");

    const response = await requestGenerateProductImage(session.sessionId, productId, payload);
    return response?.job ?? null;
  }, [session, state.credits]);

  const cleanProductImage = useCallback(async (productId, image, description = "") => {
    if (!session?.sessionId) {
      throw new Error(getLocalizationSnapshot().messages.analytics.sessionNotReady);
    }
    assertSubscriptionWritable(session);
    assertSufficientCredits(state.credits, PRODUCT_IMAGE_COST_UNITS, "product_image");

    const response = await requestCleanProductImage(session.sessionId, productId, image, description);
    const generatedProduct = response?.product ?? null;
    const nextCredits = generatedProduct?.credits ?? null;
    const currentProduct = state.products.find((product) => product.id === productId) ?? null;

    if (generatedProduct) {
      const nextProduct = {
        ...(currentProduct ?? {}),
        id: generatedProduct.shopProductId || productId,
        url: generatedProduct.url ?? null,
        urlStatus: generatedProduct.urlStatus || "pending",
      };

      await saveWebProduct(session, nextProduct).catch(() => null);
      setState((prev) => {
        const nextProducts = prev.products.map((product) => (
          product.id === productId
            ? { ...product, url: nextProduct.url, urlStatus: nextProduct.urlStatus }
            : product
        ));
        return {
          ...prev,
          credits: nextCredits ?? prev.credits,
          inventory: buildInventoryFromProducts(nextProducts),
          products: nextProducts,
        };
      });
    }

    if (!nextCredits) {
      fetchCreditBalance(session.sessionId)
        .then((balanceResponse) => {
          if (balanceResponse?.credits) {
            setState((prev) => ({ ...prev, credits: balanceResponse.credits }));
          }
        })
        .catch(() => null);
    }
    return generatedProduct;
  }, [session, state.credits, state.products]);

  const recordDebtPaymentAction = useCallback(async (debt, amount) => {
    if (!session?.sessionId) {
      throw new Error(getLocalizationSnapshot().messages.analytics.sessionNotReady);
    }
    assertSubscriptionWritable(session);

    const value = Number(amount || 0);
    if (value <= 0) {
      throw new Error(getLocalizationSnapshot().messages.common.enterPaymentAmount);
    }

    const paymentId = createClientId("dp");
    const payment = {
      amount: value,
      branchId: debt.branchId || session.selectedBranchId || "",
      createdAtIso: new Date().toISOString(),
      customerName: debt.customerName,
      debtId: debt.id,
      id: paymentId,
      saleId: debt.saleId || null,
    };

    await saveWebDebtPayment(session, payment);
    await enqueueWebSyncOperation(session, {
      aggregateId: payment.debtId,
      id: `sync-debt-payment-${paymentId}`,
      idempotencyKey: `idem_${paymentId}`,
      operationType: "DEBT_PAYMENT_RECORDED",
      payload: payment,
    });

    setState((prev) => ({
      ...prev,
      debtPayments: [mapDebtPayment(payment), ...prev.debtPayments],
      debts: prev.debts.map((item) => {
        if (item.id !== debt.id) return item;
        const nextPaid = Math.min(item.amountOwed, item.amountPaid + value);
        return {
          ...item,
          amountPaid: nextPaid,
          status: nextPaid >= item.amountOwed ? "Paid" : "Partly paid",
        };
      }),
      sync: { ...prev.sync, queuedCount: (prev.sync.queuedCount || 0) + 1, online: typeof navigator === "undefined" ? true : navigator.onLine },
    }));
    processWebSyncQueue(session).then(() => loadWorkspace()).catch(() => loadWorkspace());
  }, [loadWorkspace, session]);

  const markNotificationRead = useCallback(async (notificationId) => {
    if (!session?.sessionId || !notificationId) {
      return;
    }

    setReadNotificationIds((prev) => {
      if (prev.includes(notificationId)) {
        return prev;
      }

      const next = [...prev, notificationId];
      persistSessionNotificationReads(session.sessionId, next);
      return next;
    });
    setState((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        inbox: prev.notifications.inbox.map((item) => item.id === notificationId ? { ...item, isRead: true } : item),
      },
    }));
    await markNotificationInboxRead(session.sessionId, notificationId).catch(() => null);
  }, [session?.sessionId]);

  const markAllNotificationsRead = useCallback(async () => {
    if (!session?.sessionId) {
      return;
    }

    const next = state.notifications.inbox.map((item) => item.id);
    setReadNotificationIds(next);
    persistSessionNotificationReads(session.sessionId, next);
    setState((prev) => ({
      ...prev,
      notifications: { ...prev.notifications, inbox: prev.notifications.inbox.map((item) => ({ ...item, isRead: true })) },
    }));
    await Promise.all(state.notifications.inbox.filter((item) => !item.isRead && !item.id.startsWith("local-")).map((item) => (
      markNotificationInboxRead(session.sessionId, item.id).catch(() => null)
    )));
  }, [session?.sessionId, state.notifications.inbox]);

  const unreadNotifications = useMemo(
    () => state.notifications.inbox.filter((item) => !item.isRead && !readNotificationIds.includes(item.id)),
    [readNotificationIds, state.notifications.inbox]
  );

  const syncAndReload = useCallback(async () => {
    if (session?.sessionId) {
      await processWebSyncQueue(session).catch(() => null);
    }
    await loadWorkspace();
  }, [loadWorkspace, session]);

  const retryFailedSync = useCallback(async () => {
    if (session?.sessionId) {
      await retryFailedWebSyncOperations(session).catch(() => null);
      await processWebSyncQueue(session).catch(() => null);
    }
    await loadWorkspace();
  }, [loadWorkspace, session]);

  const deleteSyncQueueItem = useCallback(async (operationId) => {
    if (session?.sessionId) {
      await deleteWebPendingSyncQueueItem(session, operationId);
    }
    await loadWorkspace();
  }, [loadWorkspace, session]);

  const refreshCredits = useCallback(async () => {
    if (!session?.sessionId) {
      return EMPTY_CREDITS;
    }
    const balanceResponse = await fetchCreditBalance(session.sessionId);
    if (balanceResponse?.credits) {
      setState((prev) => ({ ...prev, credits: balanceResponse.credits }));
      return balanceResponse.credits;
    }
    return EMPTY_CREDITS;
  }, [session?.sessionId]);

  const setCreditBalance = useCallback((credits) => {
    if (!credits) {
      return;
    }
    setState((prev) => ({ ...prev, credits }));
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      addStock,
      archiveProduct,
      cleanProductImage,
      completeSale,
      deleteSyncQueueItem,
      generateProductImage,
      markAllNotificationsRead,
      markNotificationRead,
      recordDebtPayment: recordDebtPaymentAction,
      reload: syncAndReload,
      refreshCredits,
      retryFailedSync,
      saveProduct,
      setCreditBalance,
      setDebts,
      switchBranch: setCurrentBranch,
      unreadNotifications,
    }),
    [addStock, archiveProduct, cleanProductImage, completeSale, deleteSyncQueueItem, generateProductImage, markAllNotificationsRead, markNotificationRead, recordDebtPaymentAction, refreshCredits, retryFailedSync, saveProduct, setCreditBalance, setCurrentBranch, setDebts, state, syncAndReload, unreadNotifications]
  );

  return (
    <WorkspaceCtx.Provider value={value}>
      {children}
      <ProductLimitUpgradeDialog
        error={productLimitError}
        onOpenChange={(open) => !open && setProductLimitError(null)}
      />
    </WorkspaceCtx.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) throw new Error("useWorkspace must be used inside WorkspaceProvider");
  return ctx;
}
